import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Ship, Truck, FileText, Globe, ChevronRight } from 'lucide-react';

// Professional intermodal trucking images
const HERO_IMAGE = 'https://images.unsplash.com/photo-1766561994067-dbd575e1cff2?auto=format&fit=crop&w=1920&q=80';
const CONTAINER_YARD_BG = 'https://images.unsplash.com/photo-1611816951577-198c5dcf0e96?auto=format&fit=crop&w=1920&q=80';

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar />
      
      {/* Hero Section with Intermodal Trucking Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Professional intermodal trucking background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${HERO_IMAGE}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E1A]/95 via-[#0A0E1A]/85 to-[#0A0E1A]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A] via-transparent to-[#0A0E1A]/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase mb-6" data-testid="hero-headline">
            <span className="text-white">RELIABLE</span>
            <br />
            <span className="text-gradient-cyan">INTERMODAL</span>
            <span className="text-white"> & </span>
            <span className="text-gradient-gold">DRAYAGE</span>
            <br />
            <span className="text-white">LOGISTICS</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-300 mb-4 max-w-3xl mx-auto leading-relaxed">
            DLT, a division of Fallou Enterprise INC, provides secure and efficient container drayage, 
            import, and export logistics services across Columbus, Ohio and global trade lanes.
          </p>
          
          {/* Credentials */}
          <div className="flex flex-wrap justify-center gap-6 mb-12 text-sm">
            <div className="bg-[#0F172A]/80 backdrop-blur-sm border border-white/10 px-6 py-3 rounded-sm">
              <span className="text-slate-400">US DOT:</span>
              <span className="text-[#00A6E8] font-bold ml-2">1662372</span>
            </div>
            <div className="bg-[#0F172A]/80 backdrop-blur-sm border border-white/10 px-6 py-3 rounded-sm">
              <span className="text-slate-400">MC:</span>
              <span className="text-[#FFA726] font-bold ml-2">657445</span>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-[#00A6E8] text-[#0A0E1A] font-bold uppercase tracking-wider hover:bg-[#33B8ED] hover:scale-105 transition-all glow-cyan px-8 py-6 text-base" data-testid="submit-work-order-btn">
                Submit Work Order
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/rate-calculator">
              <Button variant="outline" className="border-[#FFA726] text-[#FFA726] font-bold uppercase tracking-wider hover:bg-[#FFA726]/10 hover:scale-105 transition-all px-8 py-6 text-base" data-testid="request-rate-btn">
                Rate Calculator
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-white/30 text-white font-bold uppercase tracking-wider hover:bg-white/5 hover:scale-105 transition-all px-8 py-6 text-base" data-testid="client-login-btn">
                Client Login
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Services Preview with Container Yard Background */}
      <section className="relative py-20 px-6 overflow-hidden" data-testid="services-preview">
        {/* Subtle container yard background */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url('${CONTAINER_YARD_BG}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E1A] via-transparent to-[#0A0E1A]" />
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-12 text-center">
            <span className="text-white">OUR </span>
            <span className="text-[#00A6E8]">SERVICES</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Import Drayage Card */}
            <div className="group relative bg-[#0F172A]/90 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden hover:border-[#00A6E8]/50 hover:glow-cyan transition-all duration-300" data-testid="service-card-import">
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1609330934087-5ff217016316?auto=format&fit=crop&w=600&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="relative z-10 p-8">
                <Ship className="text-[#00A6E8] mb-4" size={40} />
                <h3 className="text-2xl font-bold mb-3 text-white uppercase tracking-tight">Import Drayage</h3>
                <p className="text-slate-400 leading-relaxed mb-4">
                  Rail ramp pickup, delivery order processing, chassis coordination, and storage services.
                </p>
                <Link to="/services" className="text-[#00A6E8] hover:text-[#33B8ED] font-semibold flex items-center gap-2">
                  Learn More <ChevronRight size={16} />
                </Link>
              </div>
            </div>
            
            {/* Export Logistics Card */}
            <div className="group relative bg-[#0F172A]/90 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden hover:border-[#FFA726]/50 hover:glow-gold transition-all duration-300" data-testid="service-card-export">
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1580307979043-96d7c4233ed9?auto=format&fit=crop&w=600&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="relative z-10 p-8">
                <Globe className="text-[#FFA726] mb-4" size={40} />
                <h3 className="text-2xl font-bold mb-3 text-white uppercase tracking-tight">Export Logistics</h3>
                <p className="text-slate-400 leading-relaxed mb-4">
                  Empty container pickup, shipper coordination, and cutoff management.
                </p>
                <Link to="/services" className="text-[#FFA726] hover:text-[#FFB74D] font-semibold flex items-center gap-2">
                  Learn More <ChevronRight size={16} />
                </Link>
              </div>
            </div>
            
            {/* Local Drayage Card */}
            <div className="group relative bg-[#0F172A]/90 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden hover:border-white/30 transition-all duration-300" data-testid="service-card-local">
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1724525231969-173f10849bb1?auto=format&fit=crop&w=600&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="relative z-10 p-8">
                <Truck className="text-white mb-4" size={40} />
                <h3 className="text-2xl font-bold mb-3 text-white uppercase tracking-tight">Local Drayage</h3>
                <p className="text-slate-400 leading-relaxed mb-4">
                  Dedicated Columbus local drayage with structured rate agreements.
                </p>
                <Link to="/services" className="text-white hover:text-slate-300 font-semibold flex items-center gap-2">
                  Learn More <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats/Trust Section */}
      <section className="py-16 px-6 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl md:text-5xl font-bold text-[#00A6E8]">CSX</p>
              <p className="text-slate-400 text-sm mt-2 uppercase tracking-wider">Rail Partner</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold text-[#FFA726]">NS</p>
              <p className="text-slate-400 text-sm mt-2 uppercase tracking-wider">Rail Partner</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold text-white">24/7</p>
              <p className="text-slate-400 text-sm mt-2 uppercase tracking-wider">Operations</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold text-[#00A6E8]">CMH</p>
              <p className="text-slate-400 text-sm mt-2 uppercase tracking-wider">Columbus Hub</p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
